import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useLang } from "../i18n";
import Seo from "../components/Seo";
import { Eyebrow, Reveal } from "../components/motion";

export function Journal() {
  const { t, lang } = useLang();
  const [cat, setCat] = useState("");
  const { data: cats } = useQuery({ queryKey: ["jcats", lang], queryFn: () => api.get(`/journal/categories?lang=${lang}`).then((r) => r.data) });
  const { data, isLoading } = useQuery({
    queryKey: ["articles", lang, cat],
    queryFn: () => api.get(`/journal/articles?lang=${lang}${cat ? `&category=${cat}` : ""}&limit=24`).then((r) => r.data),
  });

  return (
    <div className="pt-[72px]">
      <Seo title={`${t("journal.title")} — Chef à Domicile`} description={t("journal.sub")} />
      <div className="max-w-[1300px] mx-auto px-4 sm:px-8 py-16 md:py-24">
        <Reveal>
          <Eyebrow>{t("nav.journal")}</Eyebrow>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory mb-4">{t("journal.title")}</h1>
          <p className="text-neutral-400 max-w-2xl mb-10">{t("journal.sub")}</p>
        </Reveal>
        <div className="flex flex-wrap gap-2 mb-12" data-testid="journal-categories">
          <button data-testid="journal-cat-all" onClick={() => setCat("")}
            className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${!cat ? "border-gold text-gold" : "border-white/15 text-neutral-400 hover:border-white/40"}`}>
            {t("common.all")}
          </button>
          {cats?.map((c) => (
            <button key={c.id} data-testid={`journal-cat-${c.slug}`} onClick={() => setCat(c.slug)}
              className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${cat === c.slug ? "border-gold text-gold" : "border-white/15 text-neutral-400 hover:border-white/40"}`}>
              {c.name}
            </button>
          ))}
        </div>
        {isLoading && <p className="text-neutral-500 py-16 text-center">{t("common.loading")}</p>}
        {data && data.articles.length === 0 && (
          <div className="py-24 text-center border border-white/8" data-testid="journal-empty">
            <p className="font-serif text-2xl text-neutral-400 italic">{t("journal.empty")}</p>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {data?.articles?.map((a, i) => (
            <Reveal key={a.slug} delay={(i % 3) * 0.08}>
              <Link to={`/${lang}/journal/${a.slug}`} data-testid={`journal-card-${a.slug}`} className="group block bg-coal border border-white/8 hover:border-gold/40 transition-colors h-full">
                {a.image && (
                  <div className="overflow-hidden aspect-[16/10]">
                    <img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s]" />
                  </div>
                )}
                <div className="p-6">
                  {a.category && <p className="text-[10px] uppercase tracking-[0.25em] text-gold mb-2">{a.category_name || a.category}</p>}
                  <h2 className="font-serif text-xl text-ivory group-hover:text-gold transition-colors">{a.title}</h2>
                  {a.excerpt && <p className="text-sm text-neutral-400 mt-2 leading-relaxed line-clamp-3">{a.excerpt}</p>}
                  <p className="text-xs text-gold mt-4 gold-underline inline-block">{t("common.readMore")}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}

export function JournalArticle() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const { data: a, isLoading, isError } = useQuery({
    queryKey: ["article", slug, lang],
    queryFn: () => api.get(`/journal/articles/${slug}?lang=${lang}`).then((r) => r.data),
  });

  if (isLoading) return <div className="pt-40 pb-40 text-center text-neutral-500">{t("common.loading")}</div>;
  if (isError || !a) return <div className="pt-40 pb-40 text-center text-neutral-400">{t("common.error")}</div>;

  return (
    <div className="pt-[72px]">
      <Seo title={`${a.seo_title || a.title} — Chef à Domicile`} description={a.seo_description || a.excerpt || ""} image={a.image}
        jsonLd={{ "@context": "https://schema.org", "@type": "Article", headline: a.title, image: a.image, datePublished: a.published_at }} />
      <article className="max-w-[800px] mx-auto px-4 sm:px-8 py-16 md:py-24">
        <Reveal>
          {a.category && <Eyebrow>{a.category_name || a.category}</Eyebrow>}
          <h1 className="font-serif text-4xl sm:text-5xl text-ivory mb-8 leading-tight" data-testid="article-title">{a.title}</h1>
          {a.image && <img src={a.image} alt={a.title} className="w-full aspect-[16/9] object-cover mb-10" />}
          <div className="prose-invert text-neutral-300 leading-[1.9] space-y-6 whitespace-pre-line" data-testid="article-body">
            {a.body}
          </div>
        </Reveal>
        {a.related?.length > 0 && (
          <div className="mt-20 border-t border-white/10 pt-10">
            <h2 className="font-serif text-2xl text-ivory mb-6">{t("journal.related")}</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {a.related.map((r) => (
                <Link key={r.slug} to={`/${lang}/journal/${r.slug}`} className="group border border-white/8 p-6 hover:border-gold/40 transition-colors">
                  <p className="font-serif text-lg text-ivory group-hover:text-gold transition-colors">{r.title}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
        <div className="mt-16"><Link to={`/${lang}/journal`} className="text-sm text-gold gold-underline">← {t("common.back")}</Link></div>
      </article>
    </div>
  );
}
